import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Workflow as WorkflowIcon, Trash2, MoreHorizontal, Copy, BarChart3, Check } from "lucide-react";
import type { WorkflowStatus } from "@proclaw/shared";
import { Button } from "@/components/ui/button";
import { useTenant } from "@/context/tenant-context";
import { useWorkspace } from "@/context/workspace-context";
import { useAgent } from "@/hooks/use-agents";
import { workflowsApi } from "@/api/workflows";
import { queryKeys } from "@/lib/query-keys";
import { cn } from "@/lib/utils";
import { WorkflowInsightsView } from "@/components/workflows/insights/insights-view";

/**
 * Workflows module — top-nav surface.
 *
 * Agent-aware: when the workspace has an agent selected (the chat thread's
 * agentId), this scopes the list to workflows that reference *that* agent —
 * so navigating to an agent and opening Workflows shows only its workflows,
 * not the whole tenant's. A scope switcher flips between "this agent" and
 * "all workflows". With no agent selected it falls back to the tenant-wide
 * list.
 *
 * One view since 2026-09-30: the Insights design (numbers, chart, a row per
 * workflow) with the old All list folded in. Clicking a row opens the canvas
 * at /tenants/:id/workflows/:workflowId; each row's menu has View insights
 * (the per-workflow Insights page) and, for admins, Duplicate and Delete.
 * Deleting removes the workflow and tears down its schedule triggers
 * server-side, so the agent stops running it.
 */
export function WorkflowsSurface() {
  // New Workflow, Duplicate and Delete stay tenant-admin only (Kenneth,
  // 2026-09-30), even though the server's routes also allow agent admins:
  // those routes do not check which agents a workflow uses. Changing the
  // status follows the canvas's status box (tenant and agent admins).
  const { tenantId, isAdmin, canManageAnyAgent: canChangeStatus } = useTenant();
  const { threadContext } = useWorkspace();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const agentId = threadContext.agentId ?? null;
  const sessionId = threadContext.sessionId ?? null;
  const channelId = threadContext.channelId ?? null;
  const { data: selectedAgent } = useAgent(agentId ?? "");

  // Default to the agent's workflows when an agent is in context; let the
  // user widen to the full tenant list with the scope switcher. Falls back
  // to "all" automatically when nothing is selected.
  //
  // Scope lives in the URL, not local state, so opening a workflow and
  // pressing back restores the list the user was actually looking at. Held in
  // local state it was remounted at the default, dropping anyone who had
  // widened to "All workflows" back to "This agent". Same reasoning as `mode`
  // in workspace-context.tsx: the URL is the source of truth for view state.
  const [searchParams, setSearchParams] = useSearchParams();
  const scope: "agent" | "all" = searchParams.get("wfScope") === "all" ? "all" : "agent";
  // An old link may still carry ?wfTab=insights from when All and Insights
  // were two tabs; it is ignored and lands on this one view.
  const setScope = (next: "agent" | "all") => {
    setSearchParams(
      (current) => {
        const params = new URLSearchParams(current);
        // Only the non-default needs carrying, so a plain URL stays clean.
        if (next === "all") params.set("wfScope", "all");
        else params.delete("wfScope");
        return params;
      },
      { replace: true },
    );
  };
  const scopedToAgent = !!agentId && scope === "agent";

  // The plain list still loads: it gives each row its created and updated
  // times, and the names Duplicate checks against.
  const { data: workflows = [] } = useQuery({
    queryKey: scopedToAgent
      ? queryKeys.workflowsByAgent(agentId!)
      : queryKeys.workflows.byCompany(tenantId),
    queryFn: scopedToAgent
      ? () => workflowsApi.listForAgent(agentId!)
      : () => workflowsApi.list(tenantId),
    enabled: scopedToAgent ? !!agentId : !!tenantId,
  });

  const rowDates = useMemo(
    () => new Map(workflows.map((w) => [w.id, { createdAt: w.createdAt, updatedAt: w.updatedAt }])),
    [workflows],
  );

  const [creating, setCreating] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<{ id: string; name: string } | null>(null);

  // Both the tenant-wide and agent-scoped lists can show a deleted/created
  // workflow, so refresh both keys after a mutation regardless of scope.
  const invalidateWorkflowLists = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.workflows.byCompany(tenantId) });
    if (agentId) {
      queryClient.invalidateQueries({ queryKey: queryKeys.workflowsByAgent(agentId) });
    }
  };

  // Keep the full chat identity while visiting a workflow. The global Chat
  // tab reads these URL values, so dropping the session here would return the
  // user to whichever conversation happens to be first in the channel.
  const canvasPath = (workflowId: string) => {
    const params = new URLSearchParams();
    if (agentId) params.set("agentId", agentId);
    if (sessionId) params.set("sessionId", sessionId);
    if (channelId && channelId !== "general") params.set("channelId", channelId);
    // Carry the list scope so the canvas can send the user back to the list
    // they came from. The back arrow and the Workflows tab both build a fresh
    // URL rather than using browser history, so without this they would land
    // on "This agent" no matter which list was open.
    if (scope === "all") params.set("wfScope", "all");
    const qs = params.toString();
    return `/tenants/${tenantId}/workflows/${workflowId}${qs ? `?${qs}` : ""}`;
  };

  const createWorkflow = useMutation({
    // Associate the workflow with the in-context agent so it belongs to that
    // agent from the start (server seeds an Agent node bound to it).
    mutationFn: (name: string) =>
      workflowsApi.create(tenantId, agentId ? { name, agentId } : { name }),
    onSuccess: (workflow) => {
      invalidateWorkflowLists();
      setCreating(false);
      navigate(canvasPath(workflow.id));
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : "Unknown error";
      console.error("Failed to create workflow:", err);
      alert(`Failed to create workflow: ${message}`);
    },
  });

  const deleteWorkflow = useMutation({
    mutationFn: (workflowId: string) => workflowsApi.delete(workflowId),
    onSuccess: () => {
      invalidateWorkflowLists();
      setConfirmDelete(null);
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : "Unknown error";
      console.error("Failed to delete workflow:", err);
      alert(`Failed to delete workflow: ${message}`);
    },
  });

  // Status from the row menu: the same save the canvas's status box makes.
  // The server re-arms or disarms the workflow's triggers to match.
  const setWorkflowStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: WorkflowStatus }) =>
      workflowsApi.update(id, { status }),
    onSuccess: () => {
      invalidateWorkflowLists();
      queryClient.invalidateQueries({ queryKey: ["workflow-insights"] });
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : "Unknown error";
      console.error("Failed to change workflow status:", err);
      alert(`Failed to change the status: ${message}`);
    },
  });

  // Duplicate: copy a workflow's full graph into a brand-new workflow (like the
  // New Workflow flow, landing on the canvas), but never with the same name —
  // we append "(copy)" and bump a counter if that is already taken.
  const uniqueCopyName = (base: string) => {
    const existing = new Set(workflows.map((w) => w.name));
    let candidate = `${base} (copy)`;
    let n = 2;
    while (existing.has(candidate)) candidate = `${base} (copy ${n++})`;
    return candidate.slice(0, 200);
  };

  const duplicateWorkflow = useMutation({
    mutationFn: async (row: { id: string; name: string }) => {
      const source = await workflowsApi.get(row.id);
      const created = await workflowsApi.create(tenantId, {
        name: uniqueCopyName(row.name),
        description: source.description ?? undefined,
      });
      // create() only seeds a starter node; copy the source graph over it.
      if (source.nodes.length > 0 || source.edges.length > 0) {
        await workflowsApi.update(created.id, {
          nodes: source.nodes,
          edges: source.edges,
        });
      }
      return created;
    },
    onSuccess: (created) => {
      invalidateWorkflowLists();
      navigate(canvasPath(created.id));
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : "Unknown error";
      console.error("Failed to duplicate workflow:", err);
      alert(`Failed to duplicate workflow: ${message}`);
    },
  });

  const agentName = selectedAgent?.name ?? "this agent";

  // Guided walkthrough hook: open (or create) a demo workflow and land on its
  // canvas, so the "Build a workflow" guide can show the canvas rather than
  // just pointing at the New Workflow button. Reuses an existing demo to avoid
  // piling up duplicates on repeat runs.
  useEffect(() => {
    const onDemo = () => {
      const existing = workflows.find((w) => w.name === "Demo workflow");
      if (existing) navigate(canvasPath(existing.id));
      else if (!createWorkflow.isPending) createWorkflow.mutate("Demo workflow");
    };
    window.addEventListener("proclaw:create-demo-workflow", onDemo);
    return () => window.removeEventListener("proclaw:create-demo-workflow", onDemo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflows, createWorkflow.isPending]);

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="shrink-0 border-b border-border bg-surface">
        <div className="mx-auto flex min-h-14 w-full max-w-[1216px] flex-wrap items-center justify-between gap-3 px-6 py-2 lg:px-8">
          <div className="flex min-w-0 items-center gap-3">
            <h1 className="truncate text-[15px] font-semibold tracking-tight text-text-primary">
              Workflows
            </h1>
          </div>
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
            {/* Scope switcher — only meaningful when an agent is in context. */}
            {agentId && (
              <div className="flex items-center rounded-md border border-border bg-surface p-0.5 text-xs">
                <button
                  onClick={() => setScope("agent")}
                  className={cn(
                    "rounded px-2.5 py-1 font-medium transition-colors",
                    scope === "agent"
                      ? "bg-accent/10 text-accent"
                      : "text-text-muted hover:text-text-primary",
                  )}
                >
                  This agent
                </button>
                <button
                  onClick={() => setScope("all")}
                  className={cn(
                    "rounded px-2.5 py-1 font-medium transition-colors",
                    scope === "all"
                      ? "bg-accent/10 text-accent"
                      : "text-text-muted hover:text-text-primary",
                  )}
                >
                  All workflows
                </button>
              </div>
            )}
            {isAdmin && (
              <Button
                size="sm"
                data-tour="workflow-create"
                onClick={() => setCreating(true)}
                className="gap-2"
              >
                <Plus size={14} />
                New Workflow
              </Button>
            )}
          </div>
        </div>
      </div>

      <WorkflowInsightsView
        agentId={scopedToAgent ? agentId : null}
        rowDates={rowDates}
        onOpenWorkflow={(id) => navigate(canvasPath(id))}
        renderRowMenu={(row, openInsights) => (
          <WorkflowRowMenu
            isAdmin={isAdmin}
            canChangeStatus={canChangeStatus}
            status={row.status}
            onStatus={(status) => {
              if (status !== row.status) setWorkflowStatus.mutate({ id: row.id, status });
            }}
            onInsights={openInsights}
            onDuplicate={() => duplicateWorkflow.mutate({ id: row.id, name: row.name })}
            onDelete={() => setConfirmDelete({ id: row.id, name: row.name })}
          />
        )}
        emptyState={scopedToAgent ? <AgentEmptyState agentName={agentName} /> : <EmptyState />}
      />

      {creating && (
        <NewWorkflowModal
          onCancel={() => setCreating(false)}
          onConfirm={(name) => createWorkflow.mutate(name)}
          pending={createWorkflow.isPending}
        />
      )}

      {confirmDelete && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          onClick={() => setConfirmDelete(null)}
        >
          <div
            className="w-[min(400px,calc(100vw-2rem))] rounded-lg border border-border bg-surface p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="mb-2 text-sm font-semibold text-text-primary">Delete workflow</h3>
            <p className="mb-4 text-xs text-text-secondary">
              Are you sure you want to delete{" "}
              <span className="font-medium text-text-primary">"{confirmDelete.name}"</span>?
              This removes the workflow and any of its schedules, so the
              agent will stop running it. This cannot be undone.
            </p>
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(null)}>
                Cancel
              </Button>
              <Button
                size="sm"
                variant="destructive"
                disabled={deleteWorkflow.isPending}
                onClick={() => deleteWorkflow.mutate(confirmDelete.id)}
              >
                {deleteWorkflow.isPending ? "Deleting…" : "Delete"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const STATUS_OPTIONS: Array<{ value: WorkflowStatus; label: string; dot: string }> = [
  { value: "active", label: "Active", dot: "bg-success" },
  { value: "paused", label: "Paused", dot: "bg-warning" },
  { value: "draft", label: "Draft", dot: "bg-text-muted" },
];

/**
 * The menu at the end of a workflow row. View insights for everyone; the
 * status (Active, Paused, Draft) for tenant and agent admins, as on the
 * canvas; Duplicate and Delete for tenant admins. Rendered in a portal (fixed-positioned) so the
 * table's scroll container can never clip it, flipped above the button when
 * there is no room below.
 */
function WorkflowRowMenu({
  isAdmin,
  canChangeStatus,
  status,
  onStatus,
  onInsights,
  onDuplicate,
  onDelete,
}: {
  /** Tenant admins: Duplicate and Delete. */
  isAdmin: boolean;
  /** Tenant and agent admins: the Status section. */
  canChangeStatus: boolean;
  status: string;
  onStatus: (status: WorkflowStatus) => void;
  onInsights: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const btnRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);

  // Sized like the Organisation chart's right-click menu (Kenneth wanted it
  // bigger than the old 144px, 12px-text version).
  const MENU_W = 200;
  // Approximate height, for flipping above the button near the bottom of the
  // screen: 36px per item, plus the status heading and dividers.
  const MENU_H = 12 + 36 + (canChangeStatus ? 28 + 3 * 36 + 9 : 0) + (isAdmin ? 9 + 2 * 36 : 0);

  const openMenu = () => {
    const rect = btnRef.current?.getBoundingClientRect();
    if (!rect) return;
    const flipUp = window.innerHeight - rect.bottom < MENU_H + 12;
    setMenuPos({
      top: flipUp ? rect.top - MENU_H - 4 : rect.bottom + 4,
      left: Math.max(8, rect.right - MENU_W), // right-align to the button
    });
    setMenuOpen(true);
  };

  // Close on click outside (button + popup), on Escape, and on scroll/resize
  // (a fixed menu would otherwise detach from its row).
  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (btnRef.current?.contains(t) || popRef.current?.contains(t)) return;
      setMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    const onScrollOrResize = () => setMenuOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [menuOpen]);

  const item = "flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors";

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        aria-label="Row actions"
        onClick={(e) => {
          e.stopPropagation();
          if (menuOpen) setMenuOpen(false);
          else openMenu();
        }}
        onKeyDown={(e) => e.stopPropagation()}
        // Always visible (Kenneth, 2026-09-30); it used to appear on hover only.
        className={cn(
          "flex h-7 w-7 items-center justify-center rounded text-text-muted transition-colors hover:bg-black/10 hover:text-text-primary dark:hover:bg-white/10",
          menuOpen && "bg-black/10 text-text-primary dark:bg-white/10",
        )}
      >
        <MoreHorizontal size={14} />
      </button>
      {menuOpen &&
        createPortal(
          <div
            ref={popRef}
            style={{ position: "fixed", top: menuPos.top, left: menuPos.left, width: MENU_W }}
            className="z-50 overflow-hidden rounded-xl border border-border bg-surface py-1 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => {
                setMenuOpen(false);
                onInsights();
              }}
              className={cn(item, "text-text-primary hover:bg-surface-hover")}
            >
              <BarChart3 size={14} className="text-text-secondary" />
              View insights
            </button>
            {canChangeStatus && (
              <>
                <div className="my-1 border-t border-border" />
                <p className="px-3 pb-1 pt-1.5 text-[11px] font-medium text-text-muted">Status</p>
                {STATUS_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => {
                      setMenuOpen(false);
                      onStatus(opt.value);
                    }}
                    className={cn(item, "text-text-primary hover:bg-surface-hover")}
                  >
                    <span className="flex w-3.5 justify-center">
                      <span className={cn("h-2 w-2 rounded-full", opt.dot)} />
                    </span>
                    <span className="flex-1">{opt.label}</span>
                    {status === opt.value && <Check size={14} className="text-accent" />}
                  </button>
                ))}
              </>
            )}
            {isAdmin && (
              <>
                <div className="my-1 border-t border-border" />
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false);
                    onDuplicate();
                  }}
                  className={cn(item, "text-text-primary hover:bg-surface-hover")}
                >
                  <Copy size={14} className="text-text-secondary" />
                  Duplicate
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false);
                    onDelete();
                  }}
                  className={cn(item, "text-red-500 hover:bg-red-500/10")}
                >
                  <Trash2 size={14} />
                  Delete
                </button>
              </>
            )}
          </div>,
          document.body,
        )}
    </>
  );
}

// No button here: New Workflow sits in the header above.
function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border bg-surface/50 py-16 text-center">
      <WorkflowIcon size={32} className="mb-3 text-text-muted" />
      <p className="text-sm font-medium text-text-primary">No workflows yet</p>
      <p className="mt-1 max-w-sm text-xs text-text-muted">
        Create one to automate multi-step agent pipelines — triggers, conditions, and
        agent handoffs on a visual canvas.
      </p>
    </div>
  );
}

function AgentEmptyState({ agentName }: { agentName: string }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border bg-surface/50 py-16 text-center">
      <WorkflowIcon size={32} className="mb-3 text-text-muted" />
      <p className="text-sm font-medium text-text-primary">
        No workflows for {agentName}
      </p>
      <p className="mt-1 max-w-sm text-xs text-text-muted">
        {agentName} isn't part of any workflow yet. Add an Agent node for this
        agent on a workflow's canvas, or switch to "All workflows" above to see
        the rest of the tenant's workflows.
      </p>
    </div>
  );
}

function NewWorkflowModal({
  onCancel,
  onConfirm,
  pending,
}: {
  onCancel: () => void;
  onConfirm: (name: string) => void;
  pending: boolean;
}) {
  const [name, setName] = useState("");
  const trimmed = name.trim();
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={onCancel}
    >
      <div
        className="w-[min(420px,calc(100vw-2rem))] rounded-lg border border-border bg-surface p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="mb-3 text-sm font-semibold text-text-primary">New workflow</h3>
        <label className="mb-1 block text-xs text-text-secondary">Name</label>
        <input
          autoFocus
          data-tour="workflow-name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && trimmed && !pending) onConfirm(trimmed);
            if (e.key === "Escape") onCancel();
          }}
          placeholder="Lead Qualification Pipeline"
          className="mb-4 w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-text-primary outline-none focus:border-accent"
        />
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            size="sm"
            data-tour="workflow-create-confirm"
            disabled={!trimmed || pending}
            onClick={() => onConfirm(trimmed)}
          >
            {pending ? "Creating…" : "Create"}
          </Button>
        </div>
      </div>
    </div>
  );
}
